import { Form, Formik } from 'formik';
import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'src/components/Alert';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { ColorPicker } from 'src/components/ColorPicker';
import { Divider } from 'src/components/Divider';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { uploadAppAlertImage } from 'src/models/app-alert/appalerts';
import { FormCheckbox } from 'src/shared/Forms/Fields/FormCheckbox';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import ScrollToFirstError from 'src/shared/ScrollToFirstError';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import * as Yup from 'yup';

import { AlertImageField } from './AlertImageField';
import { AppAlertPreviewPanel } from './AppAlertPreviewPanel';
import {
  BUTTON_COLOR_IDS,
  BUTTON_STYLE_IDS,
  TEXT_COLOR_IDS,
  VERTICAL_ALIGNMENT_IDS,
} from './_types';
import { buildSavePayload, readFileAsDataUrl } from './appAlertFormUtils';

import type { AppAlertFormValues, BannerPreviewVariant } from './_types';
import type { TFunction } from 'i18next';
import type { AppAlert as AppAlertType } from 'src/models/conversation/_types';

const buttonStyleLabels: Record<AppAlertFormValues['buttonStyle'], string> = {
  filled: 'Filled',
  outline: 'Outline',
  plain: 'Plain',
};

const buttonColorLabels: Record<(typeof BUTTON_COLOR_IDS)[number], string> = {
  primary: 'Primary Color',
  secondary: 'Secondary Color',
  default: 'Button',
  success: 'Success',
  error: 'Error',
};

const textColorLabels: Record<(typeof TEXT_COLOR_IDS)[number], string> = {
  default: 'Default',
  'text-darkmode': 'Light Text',
  'text2-darkmode': 'Light Text - Secondary',
  text: 'Dark Text',
  text2: 'Dark Text - Secondary',
};

const verticalAlignmentLabels: Record<
  AppAlertFormValues['verticalAlignment'],
  string
> = {
  'flex-start': 'Top',
  center: 'Center',
  'flex-end': 'Bottom',
};

const validationSchema = (t: TFunction) =>
  Yup.object({
    title: Yup.string()
      .trim()
      .required(
        t('validation.nameRequired', {
          defaultValue: 'Name is required',
        })
      ),
    heroImageUrl: Yup.string().when('alertType', ([alertType], schema) => {
      if (alertType === 'banner') {
        return schema.required(
          t('validation.shortImageRequired', {
            defaultValue: 'Short image is required',
          })
        );
      }

      return schema.required(
        t('validation.heroImageRequired', {
          defaultValue: 'Hero image is required',
        })
      );
    }),
    buttonUrl: Yup.string().test(
      'https-url',
      t('validation.httpsUrl', {
        defaultValue: 'Must start with https://',
      }),
      (value) => !value || /^https:\/\//i.test(value)
    ),
    bgImageUrl: Yup.string().when(['alertType', 'bgType'], {
      is: (alertType: string, bgType: string) =>
        alertType === 'modal' && bgType === 'image',
      then: (schema) =>
        schema.required(
          t('validation.backgroundImageRequired', {
            defaultValue: 'Background image is required',
          })
        ),
      otherwise: (schema) => schema,
    }),
  });

interface AppAlertFormProps {
  initialValues: AppAlertFormValues;
  bannersEnabled: boolean;
  isNew: boolean;
  isSaving: boolean;
  isImageUploading: boolean;
  selectedDeviceId: string;
  setSelectedDeviceId: (deviceId: string) => void;
  selectedScaleId: string;
  setSelectedScaleId: (scaleId: string) => void;
  bannerPreviewVariant: BannerPreviewVariant;
  setBannerPreviewVariant: (variant: BannerPreviewVariant) => void;
  uploadingFields: Record<'heroImageUrl' | 'bgImageUrl', boolean>;
  setUploadingFields: Dispatch<
    SetStateAction<Record<'heroImageUrl' | 'bgImageUrl', boolean>>
  >;
  onCancel: () => void;
  onSubmit: (values: Partial<AppAlertType>) => Promise<void>;
}

export const AppAlertForm = ({
  initialValues,
  bannersEnabled,
  isNew,
  isSaving,
  isImageUploading,
  selectedDeviceId,
  setSelectedDeviceId,
  selectedScaleId,
  setSelectedScaleId,
  bannerPreviewVariant,
  setBannerPreviewVariant,
  uploadingFields,
  setUploadingFields,
  onCancel,
  onSubmit,
}: AppAlertFormProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appAlertsForm',
  });
  const [previewImageUrls, setPreviewImageUrls] = useState<
    Record<'heroImageUrl' | 'bgImageUrl', string | null>
  >({
    heroImageUrl: null,
    bgImageUrl: null,
  });

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema(t)}
      onSubmit={async (values, helpers) => {
        helpers.setStatus(undefined);

        try {
          await onSubmit(buildSavePayload(values));
        } catch (error) {
          helpers.setStatus({
            formError: api.formatError(
              error,
              t('saveFailed', {
                defaultValue: 'Failed to save app alert',
              })
            ),
          });
        }
      }}
    >
      {({
        errors,
        setFieldValue,
        setFieldTouched,
        status,
        values,
        touched,
        submitForm,
      }) => {
        const showBannerFields = values.alertType === 'banner';
        const buttonStyleOptions = BUTTON_STYLE_IDS.map((id) => ({
          value: id,
          label: t(`buttonStyles.${id}`, {
            defaultValue: buttonStyleLabels[id],
          }),
        }));
        const buttonColorOptions = BUTTON_COLOR_IDS.map((id) => ({
          value: id,
          label: t(`buttonColors.${id}`, {
            defaultValue: buttonColorLabels[id],
          }),
        }));
        const textColorOptions = TEXT_COLOR_IDS.map((id) => ({
          value: id,
          label: t(`textColors.${id}`, {
            defaultValue: textColorLabels[id],
          }),
        }));
        const verticalAlignmentOptions = VERTICAL_ALIGNMENT_IDS.map((id) => ({
          value: id,
          label: t(`verticalAlignment.${id}`, {
            defaultValue: verticalAlignmentLabels[id],
          }),
        }));

        const handleImageSelect = async (
          field: 'heroImageUrl' | 'bgImageUrl',
          file: File
        ) => {
          const existingValue = values[field];
          const existingPreviewValue = previewImageUrls[field];

          setUploadingFields((current) => ({
            ...current,
            [field]: true,
          }));

          try {
            const previewUrl = await readFileAsDataUrl(file);
            setPreviewImageUrls((current) => ({
              ...current,
              [field]: previewUrl,
            }));
            setFieldValue(field, previewUrl);
            setFieldTouched(field, true, false);
            const uploadedUrl = await uploadAppAlertImage(file);
            setFieldValue(field, uploadedUrl);
          } catch (error) {
            setPreviewImageUrls((current) => ({
              ...current,
              [field]: existingPreviewValue,
            }));
            setFieldValue(field, existingValue);
            toast.error(
              api.formatError(
                error,
                t('uploadFailed', {
                  defaultValue: 'Failed to upload image',
                })
              )
            );
          } finally {
            setUploadingFields((current) => ({
              ...current,
              [field]: false,
            }));
          }
        };

        const saveDisabled = isSaving || isImageUploading;

        return (
          <Form>
            <ScrollToFirstError offset={140} />
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'minmax(0, 1fr) minmax(0, 2fr)',
                },
                alignItems: 'start',
              }}
            >
              <Card sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {!!status?.formError && (
                    <Alert severity="error">{status.formError}</Alert>
                  )}

                  {bannersEnabled && (
                    <FormSelect
                      name="alertType"
                      label={t('fields.alertStyle', {
                        defaultValue: 'Alert Style',
                      })}
                      disabled={!isNew}
                      options={[
                        {
                          value: 'modal',
                          label: t('types.modalFullScreen', {
                            defaultValue: 'Modal (Full Screen)',
                          }),
                        },
                        {
                          value: 'banner',
                          label: t('types.banner', {
                            defaultValue: 'Banner',
                          }),
                        },
                      ]}
                    />
                  )}

                  <FormTextField
                    name="title"
                    label={t('fields.name', {
                      defaultValue: 'Name',
                    })}
                    required
                  />

                  {!showBannerFields && (
                    <FormTextField
                      name="headingText"
                      label={t('fields.headingText', {
                        defaultValue: 'Heading Text',
                      })}
                      multiline
                      rows={4}
                    />
                  )}

                  {!showBannerFields ? (
                    <AlertImageField
                      label={t('fields.heroImage', {
                        defaultValue: 'Hero Image',
                      })}
                      required
                      value={previewImageUrls.heroImageUrl ?? values.heroImageUrl}
                      error={
                        touched.heroImageUrl && errors.heroImageUrl
                          ? String(errors.heroImageUrl)
                          : undefined
                      }
                      uploading={uploadingFields.heroImageUrl}
                      onSelect={(file) => {
                        void handleImageSelect('heroImageUrl', file);
                      }}
                      onRemove={() => {
                        setPreviewImageUrls((current) => ({
                          ...current,
                          heroImageUrl: null,
                        }));
                        setFieldValue('heroImageUrl', '');
                        setFieldTouched('heroImageUrl', true, false);
                      }}
                    />
                  ) : (
                    <>
                      <AlertImageField
                        label={t('fields.bannerShortImage', {
                          defaultValue: 'Banner Short Image',
                        })}
                        required
                        value={
                          previewImageUrls.heroImageUrl ?? values.heroImageUrl
                        }
                        error={
                          touched.heroImageUrl && errors.heroImageUrl
                            ? String(errors.heroImageUrl)
                            : undefined
                        }
                        helperText={t('fields.bannerShortImageHelper', {
                          defaultValue:
                            'This image will be displayed on the Home page in the app.',
                        })}
                        uploading={uploadingFields.heroImageUrl}
                        aspectRatio="3.2 / 1"
                        onSelect={(file) => {
                          void handleImageSelect('heroImageUrl', file);
                        }}
                        onRemove={() => {
                          setPreviewImageUrls((current) => ({
                            ...current,
                            heroImageUrl: null,
                          }));
                          setFieldValue('heroImageUrl', '');
                          setFieldTouched('heroImageUrl', true, false);
                        }}
                      />

                      <AlertImageField
                        label={t('fields.bannerTallImage', {
                          defaultValue: 'Banner Tall Image',
                        })}
                        value={previewImageUrls.bgImageUrl ?? values.bgImageUrl}
                        helperText={t('fields.bannerTallImageHelper', {
                          defaultValue:
                            'The tall banner image will be displayed on the Challenges page in the app. If you do not add a tall banner image, the Challenges page will show the short banner image instead.',
                        })}
                        uploading={uploadingFields.bgImageUrl}
                        aspectRatio="16 / 9"
                        onSelect={(file) => {
                          void handleImageSelect('bgImageUrl', file);
                        }}
                        onRemove={() => {
                          setPreviewImageUrls((current) => ({
                            ...current,
                            bgImageUrl: null,
                          }));
                          setFieldValue('bgImageUrl', '');
                          setFieldTouched('bgImageUrl', true, false);
                        }}
                      />
                    </>
                  )}

                  {!showBannerFields && (
                    <>
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 2,
                          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        }}
                      >
                        <FormSelect
                          name="buttonStyle"
                          label={t('fields.buttonStyle', {
                            defaultValue: 'Button Style',
                          })}
                          options={buttonStyleOptions}
                        />
                        <FormSelect
                          name="buttonColor"
                          label={t('fields.buttonColor', {
                            defaultValue: 'Button Color',
                          })}
                          options={buttonColorOptions}
                        />
                      </Box>

                      <FormTextField
                        name="buttonCopy"
                        label={t('fields.buttonText', {
                          defaultValue: 'Button Text',
                        })}
                      />
                    </>
                  )}

                  <FormTextField
                    name="buttonUrl"
                    label={t(
                      showBannerFields
                        ? 'fields.bannerActionUrl'
                        : 'fields.buttonActionUrl',
                      {
                        defaultValue: showBannerFields
                          ? 'Banner Action URL'
                          : 'Button Action URL',
                      }
                    )}
                    placeholder={t('fields.httpsPlaceholder', {
                      defaultValue: 'https://',
                    })}
                  />

                  {!showBannerFields && (
                    <>
                      <FormTextField
                        name="textBlock"
                        label={t('fields.textBlock', {
                          defaultValue: 'Text Block',
                        })}
                        multiline
                        rows={4}
                      />

                      <FormSelect
                        name="textColor"
                        label={t('fields.textColor', {
                          defaultValue: 'Text Color',
                        })}
                        options={textColorOptions}
                      />

                      <FormSelect
                        name="bgType"
                        label={t('fields.backgroundType', {
                          defaultValue: 'Background Type',
                        })}
                        options={[
                          {
                            value: 'color',
                            label: t('backgroundTypes.color', {
                              defaultValue: 'Color',
                            }),
                          },
                          {
                            value: 'image',
                            label: t('backgroundTypes.image', {
                              defaultValue: 'Image',
                            }),
                          },
                        ]}
                      />

                      {values.bgType === 'image' ? (
                        <AlertImageField
                          label={t('fields.backgroundImage', {
                            defaultValue: 'Background Image',
                          })}
                          value={values.bgImageUrl}
                          error={
                            touched.bgImageUrl && errors.bgImageUrl
                              ? String(errors.bgImageUrl)
                              : undefined
                          }
                          uploading={uploadingFields.bgImageUrl}
                          height="200px"
                          onSelect={(file) => {
                            void handleImageSelect('bgImageUrl', file);
                          }}
                          onRemove={() => {
                            setFieldValue('bgImageUrl', '');
                            setFieldValue('highContrastButton', false);
                            setFieldTouched('bgImageUrl', true, false);
                          }}
                        />
                      ) : (
                        <Stack spacing={1}>
                          <Typography variant="body2">
                            {t('fields.backgroundColor', {
                              defaultValue: 'Background Color',
                            })}
                          </Typography>
                          <ColorPicker
                            color={values.bgColor || '#FFFFFF'}
                            showHex
                            onChange={(color) => {
                              setFieldValue('bgColor', color);
                            }}
                          />
                        </Stack>
                      )}

                      {values.bgType === 'image' && (
                        <FormCheckbox
                          name="highContrastButton"
                          label={t('fields.highContrastCloseButton', {
                            defaultValue: 'High contrast close button',
                          })}
                        />
                      )}

                      <FormSelect
                        name="verticalAlignment"
                        label={t('fields.verticalAlignment', {
                          defaultValue: 'Vertical Alignment',
                        })}
                        options={verticalAlignmentOptions}
                      />

                      <FormCheckbox
                        name="hideCloseButton"
                        label={t('fields.hideCloseButton', {
                          defaultValue: 'Hide Close Button',
                        })}
                        caption={t('fields.hideCloseButtonNote', {
                          defaultValue:
                            'Only enable this if the alert content itself provides a clear way to dismiss the screen.',
                        })}
                      />
                    </>
                  )}

                  <Divider />

                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                      variant="bordered"
                      color="neutral"
                      onClick={onCancel}
                      disabled={saveDisabled}
                    >
                      {t('actions.cancel', {
                        defaultValue: 'Cancel',
                      })}
                    </Button>
                    <Button
                      variant="prominent"
                      color="primary"
                      onClick={() => {
                        void submitForm();
                      }}
                      loading={isSaving}
                      disabled={saveDisabled}
                    >
                      {t('actions.save', {
                        defaultValue: 'Save',
                      })}
                    </Button>
                  </Stack>
                </Stack>
              </Card>

              <AppAlertPreviewPanel
                values={values}
                previewImageUrls={previewImageUrls}
                bannerPreviewVariant={bannerPreviewVariant}
                setBannerPreviewVariant={setBannerPreviewVariant}
                selectedDeviceId={selectedDeviceId}
                setSelectedDeviceId={setSelectedDeviceId}
                selectedScaleId={selectedScaleId}
                setSelectedScaleId={setSelectedScaleId}
              />
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};
